"use client"

import Link from "next/link"
import {
  IconChecks,
  IconChevronRight,
  IconUser,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import type { Reminder } from "@/types"

interface FollowUpsWidgetProps {
  /**
   * Due + unread reminders for the signed-in agent, newest deadline first —
   * the `getDueReminders()` slice fetched by the parent variant (so the KPI
   * band and this rail agree on one number and one request). Never longer than
   * the parent's limit.
   */
  reminders: Reminder[]
  isLoading: boolean
  /** Extra classes merged onto the panel (grid span). */
  className?: string
  legacy?: boolean
}

/**
 * "Follow-ups due" rail — the agent dashboard's private widget.
 *
 * Presentational by design: the parent (`agent-dashboard.tsx`) owns the fetch
 * and the `reminders.view` gate, because the same rows also drive the headline
 * KPI tile. This component never requests anything, so it cannot 403.
 *
 * Rows are non-interactive except for the related-lead link — a whole-row
 * anchor would nest interactive elements once the lead link is present. The
 * panel footer carries the single "see everything" exit to /notifications.
 */
export function FollowUpsWidget({
  reminders,
  isLoading,
  className,
  legacy = false,
}: FollowUpsWidgetProps) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()

  const footer = (
    <div className="mt-3 border-t border-border pt-3">
      <Link
        href="/notifications"
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        {t("followUps.viewAll")}
        <IconChevronRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t(legacy ? "followUps.legacyTitle" : "followUps.title")} className={className}>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="h-3 w-14 shrink-0" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (reminders.length === 0) {
    return (
      <ApolloPanel eyebrow={t(legacy ? "followUps.legacyTitle" : "followUps.title")} className={className}>
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="mb-3 rounded-full bg-emerald-500/10 p-3">
            <IconChecks className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("followUps.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("followUps.empty.body")}
          </p>
        </div>
        {footer}
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel
      eyebrow={t(legacy ? "followUps.legacyTitle" : "followUps.title")}
      actions={
        <Badge variant="destructive" className="text-[10px]">
          {t("followUps.count", { count: reminders.length })}
        </Badge>
      }
      className={className}
    >
      <ul className="space-y-1">
        {reminders.map((reminder) => {
          const dueAt = new Date(reminder.remind_at)
          const isValidDate = !Number.isNaN(dueAt.getTime())
          // Server-computed flag (same signal the /notifications table styles
          // on) — no clock read during render, which the purity rule forbids
          // and which would also drift from the backend's timezone.
          const isOverdue = reminder.is_due

          return (
            <li
              key={reminder.id}
              className="flex items-start gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/50"
            >
              <span
                className={cn(
                  "mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full",
                  isOverdue ? "bg-destructive" : "bg-primary",
                )}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{reminder.title}</p>
                {reminder.description && (
                  <p className="mt-0.5 line-clamp-1 text-[11px] text-text-dim">
                    {reminder.description}
                  </p>
                )}
                {reminder.lead && (
                  <Link
                    href={`/leads/${reminder.lead.id}`}
                    className="mt-1 inline-flex max-w-full items-center gap-1 text-[11px] text-primary hover:underline"
                  >
                    <IconUser className="h-3 w-3 shrink-0" />
                    <span className="truncate">{reminder.lead.full_name}</span>
                  </Link>
                )}
              </div>
              {isValidDate && (
                <span
                  className={cn(
                    "shrink-0 text-[11px] tabular-nums",
                    isOverdue
                      ? "font-medium text-destructive"
                      : "text-text-dim",
                  )}
                  title={formatter.dateTime(dueAt, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                >
                  {formatter.relativeTime(dueAt)}
                </span>
              )}
            </li>
          )
        })}
      </ul>
      {footer}
    </ApolloPanel>
  )
}
